import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { 
  Calendar, Clock, Package, TrendingUp, Play, Pause, Snowflake,
  BarChart3, ListFilter, CalendarDays, Timer, Coffee, Sun, 
  CheckCircle, AlertTriangle, Minus, Plus, RefreshCw
} from 'lucide-react';
import TrocarOperacaoModal from './TrocarOperacaoModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Formatar segundos para "Xh Xm"
const formatTempo = (segundos) => {
  if (!segundos || segundos === 0) return '0m';
  const horas = Math.floor(segundos / 3600);
  const minutos = Math.floor((segundos % 3600) / 60);
  if (horas > 0) return `${horas}h ${minutos}m`;
  return `${minutos}m`;
};

// Formatar data ISO para "HH:MM"
const formatHora = (isoString) => {
  if (!isoString) return '-';
  const date = new Date(isoString);
  return date.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
};

// Normalizar registo para shape comum
const normalizeRegisto = (r) => ({
  id: r.id,
  isPontual: false,
  codigo: r.operacao_codigo || '—',
  nome: r.operacao_nome || '—',
  numero_of: r.numero_of || '—',
  marca: r.marca || '',
  artigo: r.artigo || '',
  lote_codigo: r.lote_codigo || '',
  lote: r.lote_codigo || (r.lote_id ? 'Lote' : null),
  hora_inicio: r.hora_inicio,
  hora_fim: r.hora_fim,
  tempo_standard: r.tempo_standard || 0,
  tempo_produtivo: r.tempo_produtivo_segundos || 0,
  quantidade: r.quantidade_produzida || 0,
  eficiencia: r.eficiencia,
});

// Normalizar pontual para shape comum
const normalizePontual = (p) => ({
  id: p.id,
  isPontual: true,
  codigo: (p.operacoes_codigos && p.operacoes_codigos[0]) || 'PONT',
  nome: p.tarefa_nome || 'Tarefa Pontual',
  numero_of: p.categoria_nome || '—',
  marca: p.marca || '',
  artigo: '',
  lote: null,
  categoria_cor: p.categoria_cor,
  observacoes: p.observacoes,
  hora_inicio: p.inicio_em,
  hora_fim: p.concluido_em,
  tempo_standard: 0,
  tempo_produtivo: p.duracao_segundos || 0,
  quantidade: p.quantidade || 0,
  eficiencia: null,
});

// Gerar atalhos para os últimos 5 dias
const getAtalhosUltimosDias = () => {
  const atalhos = [];
  const hoje = new Date();
  for (let i = 0; i <= 4; i++) {
    const data = new Date(hoje);
    data.setDate(data.getDate() - i);
    const dataStr = data.toISOString().split('T')[0];
    let label = i === 0 ? 'Hoje' : i === 1 ? 'Ontem' : `${['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'][data.getDay()]} ${data.getDate()}`;
    atalhos.push({ data: dataStr, label });
  }
  return atalhos;
};

export default function HistoricoOperadorModal({ operador, isOpen, onClose }) {
  const [loading, setLoading] = useState(false);
  const [acaoEmCurso, setAcaoEmCurso] = useState(null); // null, 'concluir', 'parcial', 'congelar', 'anomalias'
  const [quantidade, setQuantidade] = useState('');
  const [comAnomalias, setComAnomalias] = useState(false);
  const [quantidadeAnomalia, setQuantidadeAnomalia] = useState('');
  const [periodo, setPeriodo] = useState('dia');
  const [dataEspecifica, setDataEspecifica] = useState('');
  const [dados, setDados] = useState(null);
  const [vistaAtiva, setVistaAtiva] = useState('lista');
  const [trocarOpRegisto, setTrocarOpRegisto] = useState(null); // { registo_id, registo_info } para abrir TrocarOperacaoModal

  const atalhosUltimosDias = getAtalhosUltimosDias();

  // Listas combinadas e filtradas para Lista/Operações/Pontuais
  const itensCombinados = React.useMemo(() => {
    if (!dados) return [];
    const regs = (dados.historico || []).map(normalizeRegisto);
    const pts = (dados.tarefas_pontuais || []).map(normalizePontual);
    return [...regs, ...pts].sort((a, b) => {
      const ta = new Date(a.hora_inicio || 0).getTime();
      const tb = new Date(b.hora_inicio || 0).getTime();
      return ta - tb;
    });
  }, [dados]);
  const itensOperacoes = itensCombinados.filter(i => !i.isPontual);
  const itensPontuais = itensCombinados.filter(i => i.isPontual);

  const tempoProdutivoTotal = (dados?.resumo?.total_produtivo_segundos || 0) + (dados?.resumo?.tempo_pontuais_segundos || 0);
  const pecasTotal = (dados?.resumo?.total_quantidade || 0) + (dados?.resumo?.total_pecas_pontuais || 0);

  // Renderer comum para Lista / Operações / Pontuais
  const renderItem = (it, i) => {
    const tempoPrevisto = (it.tempo_standard || 0) * (it.quantidade || 0);
    const tempoReal = it.tempo_produtivo || 0;
    const temVariacao = tempoPrevisto > 0 ? ((tempoReal - tempoPrevisto) / tempoPrevisto * 100) : 0;
    const efic = it.eficiencia;
    return (
      <Card key={it.id || i} className="p-3 hover:bg-stone-50" data-testid={it.isPontual ? `pontual-${it.id}` : `registo-${it.id}`}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className={`font-mono text-xs px-1.5 py-0.5 rounded font-bold ${it.isPontual ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                {it.codigo}
              </span>
              <span className="text-sm font-medium truncate">{it.nome}</span>
              {it.isPontual && (
                <span className="text-[9px] px-1.5 py-0.5 rounded font-bold bg-purple-50 text-purple-600 border border-purple-200">PONTUAL</span>
              )}
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-stone-500">
              <span>{it.isPontual ? 'Categoria' : 'OF'}: <span className="font-medium text-stone-700">{it.numero_of}</span></span>
              {it.marca && <span>Marca: <span className="font-medium text-stone-700">{it.marca}</span></span>}
              {it.artigo && <span>Modelo: <span className="font-medium text-stone-700">{it.artigo}</span></span>}
              {it.lote && <span className="bg-stone-100 px-1.5 py-0.5 rounded font-medium text-stone-700">Lote: {it.lote_codigo || it.lote}</span>}
            </div>
            {it.observacoes && (
              <p className="text-xs italic text-stone-600 mt-1 truncate">"{it.observacoes}"</p>
            )}
          </div>
          <div className="flex items-center gap-4 text-right shrink-0">
            <div className="text-xs">
              <div className="flex items-center gap-1 text-stone-500">
                <Play className="h-3 w-3 text-green-500" />
                <span>{formatHora(it.hora_inicio)}</span>
              </div>
              <div className="flex items-center gap-1 text-stone-500">
                <Pause className="h-3 w-3 text-red-500" />
                <span>{formatHora(it.hora_fim)}</span>
              </div>
            </div>
            <div className="text-xs border-l pl-3">
              <div className="flex items-center gap-1 text-stone-500">
                <Timer className="h-3 w-3" />
                <span>Prev: {it.isPontual ? '—' : formatTempo(tempoPrevisto)}</span>
              </div>
              <div className={`flex items-center gap-1 ${it.isPontual ? 'text-stone-600' : (temVariacao > 20 ? 'text-red-600' : temVariacao < -10 ? 'text-green-600' : 'text-stone-600')}`}>
                <Clock className="h-3 w-3" />
                <span>Real: {formatTempo(tempoReal)}</span>
              </div>
            </div>
            <div className="border-l pl-3">
              <p className={`font-bold text-lg ${it.isPontual ? 'text-purple-600' : ''}`}>{it.quantidade}</p>
              <p className="text-xs text-stone-500">un.</p>
            </div>
            {it.isPontual ? (
              <span className="px-2 py-1 rounded text-sm font-bold bg-purple-50 text-purple-600 border border-purple-200">—</span>
            ) : (
              <span className={`px-2 py-1 rounded text-sm font-bold ${getEficienciaColor(efic)}`}>{efic}%</span>
            )}
            {!it.isPontual && it.id && (
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  setTrocarOpRegisto({
                    registo_id: it.id,
                    registo_info: {
                      operador_nome: operador?.nome_completo || operador?.username,
                      lote_codigo: it.lote_codigo || null,
                      of_numero: it.numero_of,
                    },
                  });
                }}
                className="h-8 w-8 p-0 text-amber-600 hover:text-amber-800 hover:bg-amber-50 border border-transparent hover:border-amber-200"
                title="Trocar operação"
                data-testid={`trocar-op-list-${it.id}`}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </Card>
    );
  };

  useEffect(() => {
    if (isOpen && operador) {
      loadHistorico();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, operador, periodo, dataEspecifica]);

  // Auto-refresh enquanto o modal está aberto: 8s. Permite reflectir alterações
  // efectuadas no app do operador (ex: terminar a operação) sem precisar fechar/reabrir.
  useEffect(() => {
    if (!isOpen || !operador?.id) return;
    const it = setInterval(() => { loadHistorico(); }, 8000);
    return () => clearInterval(it);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, operador?.id]);

  const loadHistorico = async () => {
    if (!operador?.id) return;
    setLoading(true);
    try {
      const params = { periodo };
      if (dataEspecifica) params.data = dataEspecifica;
      const response = await axios.get(`${API}/operadores/${operador.id}/historico-operacoes`, { params });
      setDados(response.data);
    } catch (error) {
      toast.error('Erro ao carregar histórico');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setAcaoEmCurso(null);
    setQuantidade('');
    setComAnomalias(false);
    setQuantidadeAnomalia('');
  };

  // Auto-reduzir peças boas quando anomalias aumentam
  const ec = dados?.em_curso;
  const maxDisponivel = ec?.quantidade_disponivel || 0;
  
  const handleAnomaliaChange = (newAnomaliaStr) => {
    const newAnomalia = parseInt(newAnomaliaStr) || 0;
    const clampedAnomalia = Math.max(0, Math.min(newAnomalia, maxDisponivel));
    setQuantidadeAnomalia(clampedAnomalia.toString());
    // Auto-ajustar boas: disponível - anomalias
    const newBoas = Math.max(0, maxDisponivel - clampedAnomalia);
    setQuantidade(newBoas.toString());
  };

  // Concluir operação
  const handleConcluir = async () => {
    if (!dados?.em_curso?.id) return;
    const qtd = parseInt(quantidade) || 0;
    const qtdAnomalias = parseInt(quantidadeAnomalia) || 0;
    const pecasBoas = Math.max(0, qtd - qtdAnomalias);
    
    // Determinar tipo_conclusao com base na ação do gestor
    const tipoConclusao = acaoEmCurso === 'parcial' ? 'parcial' : 'concluido';
    
    setLoading(true);
    try {
      // Se tem anomalias, registar primeiro (sem classificação - será feita depois pelo gestor)
      if (comAnomalias && qtdAnomalias > 0) {
        await axios.post(`${API}/anomalias-producao`, {
          of_id: dados.em_curso.of_id,
          lote_id: dados.em_curso.lote_id,
          operacao_id: dados.em_curso.operacao_id,
          operador_id: operador.id,
          tipo: 'pendente_classificacao',
          tipo_anomalia_id: null,
          tipo_nome: 'Pendente de Classificação',
          quantidade: qtdAnomalias,
          observacoes: 'Anomalia registada pelo gestor via painel - aguarda classificação'
        });
      }
      
      await axios.post(`${API}/producao/parar`, {
        registo_id: dados.em_curso.id,
        quantidade_produzida: pecasBoas,
        tipo_conclusao: tipoConclusao,
        observacoes: `Operação ${tipoConclusao === 'concluido' ? 'concluída' : 'marcada parcial'} pelo gestor${qtdAnomalias > 0 ? ` (${qtdAnomalias} anomalias)` : ''}`
      });
      
      // Notificar o operador da ação do gestor
      try {
        await axios.post(`${API}/notificacoes`, {
          tipo: 'gestor_intervenção',
          destinatario_id: operador.id,
          titulo: tipoConclusao === 'concluido' ? 'Operação Concluída pelo Gestor' : 'Operação Parcial pelo Gestor',
          mensagem: `A operação ${dados.em_curso.operacao_codigo} - ${dados.em_curso.operacao_nome} foi ${tipoConclusao === 'concluido' ? 'concluída' : 'marcada como parcial'} pelo gestor com ${pecasBoas} peça(s) boas.${qtdAnomalias > 0 ? ` ${qtdAnomalias} anomalia(s) registada(s).` : ''}`,
          dados: {
            operacao_codigo: dados.em_curso.operacao_codigo,
            operacao_nome: dados.em_curso.operacao_nome,
            numero_of: dados.em_curso.numero_of,
            quantidade: qtd,
            tipo_conclusao: tipoConclusao
          }
        });
      } catch (notifErr) {
        console.warn('Aviso: Não foi possível notificar o operador', notifErr);
      }
      
      toast.success(`Operação ${tipoConclusao === 'concluido' ? 'concluída' : 'parcial'} com ${pecasBoas} peças boas${comAnomalias && qtdAnomalias > 0 ? ` e ${qtdAnomalias} anomalia(s)` : ''}`);
      resetForm();
      await loadHistorico();
    } catch (error) {
      const detail = error.response?.data?.detail || error.message;
      // Caso comum: o operador acabou de terminar/parar a operação enquanto o gestor estava na UI
      const opEntretantoTerminada = /não encontrada|nao encontrada|já termina|ja termina/i.test(String(detail));
      if (opEntretantoTerminada) {
        toast.info('O operador entretanto terminou a operação. A atualizar...');
        resetForm();
        await loadHistorico();
      } else {
        toast.error('Erro: ' + detail);
      }
    } finally {
      setLoading(false);
    }
  };

  // Congelar operação
  const handleCongelar = async () => {
    if (!dados?.em_curso?.id || !operador?.id) return;
    setLoading(true);
    try {
      await axios.post(`${API}/producao/congelar`, { 
        operador_id: operador.id,
        tempo_decorrido: dados.em_curso.tempo_decorrido_segundos || 0
      });
      
      // Notificar o operador
      try {
        await axios.post(`${API}/notificacoes`, {
          tipo: 'gestor_intervenção',
          destinatario_id: operador.id,
          titulo: 'Operação Congelada pelo Gestor',
          mensagem: `A operação ${dados.em_curso.operacao_codigo} - ${dados.em_curso.operacao_nome} foi congelada pelo gestor.`,
          dados: {
            operacao_codigo: dados.em_curso.operacao_codigo,
            operacao_nome: dados.em_curso.operacao_nome,
            numero_of: dados.em_curso.numero_of,
            tipo_conclusao: 'congelado'
          }
        });
      } catch (notifErr) {
        console.warn('Aviso: Não foi possível notificar o operador', notifErr);
      }
      
      toast.success('Operação congelada');
      resetForm();
      await loadHistorico();
    } catch (error) {
      toast.error('Erro: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const getEficienciaColor = (eficiencia) => {
    if (eficiencia >= 100) return 'text-green-600 bg-green-100';
    if (eficiencia >= 80) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  if (!operador) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
        <DialogHeader className="pb-2">
          <DialogTitle className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-xl">
              {operador.nome_completo?.charAt(0) || '?'}
            </div>
            <div>
              <span className="text-xl">{operador.nome_completo || operador.username}</span>
              <p className="text-sm text-stone-500 font-normal">Histórico de Operações</p>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Filtros */}
        <div className="flex flex-wrap items-center gap-2 py-2 border-b">
          <span className="text-xs text-stone-500 mr-1">Rápido:</span>
          {atalhosUltimosDias.map((a, i) => (
            <Button key={a.data} size="sm" variant={dataEspecifica === a.data ? 'default' : 'outline'}
              onClick={() => { setDataEspecifica(a.data); setPeriodo('dia'); }}
              className={`h-8 px-3 ${i === 0 && !dataEspecifica ? 'bg-blue-500 text-white' : ''}`}>
              {a.label}
            </Button>
          ))}
          <div className="h-6 w-px bg-stone-200 mx-1" />
          <Button size="sm" variant={periodo === 'semana' && !dataEspecifica ? 'default' : 'outline'}
            onClick={() => { setPeriodo('semana'); setDataEspecifica(''); }} className="h-8">Semana</Button>
          <Button size="sm" variant={periodo === 'mes' && !dataEspecifica ? 'default' : 'outline'}
            onClick={() => { setPeriodo('mes'); setDataEspecifica(''); }} className="h-8">Mês</Button>
          <div className="h-6 w-px bg-stone-200 mx-1" />
          <Input type="date" value={dataEspecifica} onChange={(e) => { setDataEspecifica(e.target.value); setPeriodo('dia'); }}
            className="w-36 h-8 text-sm" />
        </div>

        {/* Operação em Curso */}
        {ec && (
          <Card className={`p-4 border-2 ${
            acaoEmCurso ? 'bg-stone-50 border-stone-300' : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300'
          }`}>
            {!acaoEmCurso ? (
              // Vista normal
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center animate-pulse ${ec.em_pausa ? 'bg-orange-500' : 'bg-green-500'}`}>
                      {ec.em_pausa ? <Pause className="h-5 w-5 text-white" /> : <Play className="h-5 w-5 text-white" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm bg-green-200 text-green-800 px-2 py-0.5 rounded font-bold">{ec.operacao_codigo}</span>
                        <span className="font-semibold text-green-800">{ec.operacao_nome}</span>
                      </div>
                      <p className="text-sm text-green-700">OF: {ec.numero_of} • Lote: {ec.lote_codigo || ec.lote_id?.slice(0,8)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-700">{formatTempo(ec.tempo_decorrido_segundos)}</p>
                    <p className="text-xs text-green-600">Desde {formatHora(ec.hora_inicio)}</p>
                  </div>
                </div>
                
                {/* Informação completa da operação em curso */}
                <div className="space-y-2 bg-white/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-bold text-stone-800">{ec.operacao_codigo} — {ec.operacao_nome}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-xs">
                    <div><span className="text-stone-400">OF:</span> <span className="font-semibold">{ec.numero_of}</span></div>
                    <div><span className="text-stone-400">Marca:</span> <span className="font-semibold">{ec.marca || '-'}</span></div>
                    <div><span className="text-stone-400">Artigo:</span> <span className="font-semibold">{ec.artigo || '-'}</span></div>
                    <div><span className="text-stone-400">Lote:</span> <span className="font-semibold">{ec.lote_codigo || '-'}</span></div>
                  </div>
                  <div className="grid grid-cols-4 gap-2 pt-1 border-t">
                    <div className="text-center">
                      <p className="text-[10px] text-stone-400">Qtd Lote</p>
                      <p className="font-bold text-stone-800">{ec.quantidade_lote || 0}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] text-stone-400">Produzidas</p>
                      <p className="font-bold text-blue-600">{ec.quantidade_ja_produzida || 0}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] text-stone-400">Disponível</p>
                      <p className="font-bold text-green-600">{ec.quantidade_disponivel || 0}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] text-stone-400">Tempo</p>
                      <p className="font-bold text-blue-600">{formatTempo(ec.tempo_decorrido_segundos)}</p>
                    </div>
                  </div>
                </div>

                {/* Botões de Acção */}
                <div className="grid grid-cols-5 gap-2">
                  <Button className="bg-green-600 hover:bg-green-700 gap-1" onClick={() => {
                    setQuantidade((ec.quantidade_disponivel || 0).toString());
                    setAcaoEmCurso('concluir');
                  }}>
                    <CheckCircle className="h-4 w-4" /> Completo
                  </Button>
                  <Button variant="outline" className="border-blue-400 text-blue-600 hover:bg-blue-50 gap-1" onClick={() => {
                    setQuantidade('');
                    setAcaoEmCurso('parcial');
                  }}>
                    <Package className="h-4 w-4" /> Parcial
                  </Button>
                  <Button variant="outline" className="border-orange-400 text-orange-600 hover:bg-orange-50 gap-1" onClick={() => {
                    setQuantidade((ec.quantidade_disponivel || 0).toString());
                    setComAnomalias(true);
                    setAcaoEmCurso('anomalias');
                  }}>
                    <AlertTriangle className="h-4 w-4" /> Anomalias
                  </Button>
                  <Button variant="outline" className="border-cyan-400 text-cyan-600 hover:bg-cyan-50 gap-1" onClick={() => setAcaoEmCurso('congelar')}>
                    <Snowflake className="h-4 w-4" /> Congelar
                  </Button>
                  <Button
                    variant="outline"
                    className="border-amber-400 text-amber-700 hover:bg-amber-50 gap-1"
                    onClick={() => setTrocarOpRegisto({
                      registo_id: ec.registo_id || ec.producao_id || ec.id,
                      registo_info: {
                        operador_nome: operador?.nome_completo || operador?.username,
                        lote_codigo: ec.lote_codigo,
                        of_numero: ec.numero_of || ec.of_numero,
                      },
                    })}
                    data-testid="trocar-operacao-btn"
                    title="Trocar operação (corrigir selecção do operador)"
                  >
                    <RefreshCw className="h-4 w-4" /> Trocar
                  </Button>
                </div>
              </div>
            ) : acaoEmCurso === 'congelar' ? (
              // Congelar
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-cyan-700">
                  <Snowflake className="h-5 w-5" />
                  <span className="font-semibold">Congelar: {ec.operacao_codigo} - {ec.operacao_nome}</span>
                </div>
                <div className="bg-cyan-50 rounded-lg p-4 text-sm text-cyan-800">
                  A operação será pausada. O operador poderá iniciar outra tarefa e retomar esta depois.
                  <p className="mt-2 font-semibold">Tempo acumulado: {formatTempo(ec.tempo_decorrido_segundos)}</p>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={resetForm} disabled={loading} className="flex-1">Cancelar</Button>
                  <Button className="flex-1 bg-cyan-600 hover:bg-cyan-700 gap-1" onClick={handleCongelar} disabled={loading}>
                    {loading ? 'A congelar...' : <><Snowflake className="h-4 w-4" /> Confirmar</>}
                  </Button>
                </div>
              </div>
            ) : (
              // Concluir / Parcial / Anomalias - Layout Compacto
              <div className="space-y-2">
                {/* Header com info completa */}
                <div className="bg-blue-50 rounded-lg p-2 space-y-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {acaoEmCurso === 'anomalias' ? <AlertTriangle className="h-4 w-4 text-orange-600" /> : <CheckCircle className="h-4 w-4 text-green-600" />}
                      <span className="font-semibold text-sm text-stone-700">
                        {acaoEmCurso === 'anomalias' ? 'Concluir c/ Anomalias' : acaoEmCurso === 'parcial' ? 'Parcial' : 'Completo'}
                      </span>
                    </div>
                    <span className="text-xs text-blue-600">{formatTempo(ec.tempo_decorrido_segundos)}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-3 text-xs">
                    <span><b>{ec.operacao_codigo}</b> — {ec.operacao_nome}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-3 text-xs text-stone-500">
                    <span>OF: <b className="text-stone-700">{ec.numero_of}</b></span>
                    {ec.marca && <span>Marca: <b className="text-stone-700">{ec.marca}</b></span>}
                    {ec.artigo && <span>Artigo: <b className="text-stone-700">{ec.artigo}</b></span>}
                    <span>Lote: <b className="text-stone-700">{ec.lote_codigo || '-'}</b></span>
                  </div>
                  <div className="flex flex-wrap gap-x-3 text-xs">
                    <span>Qt Lote: <b className="text-stone-700">{ec.quantidade_lote || 0}</b></span>
                    <span className="text-green-600 font-bold">Disponível: {ec.quantidade_disponivel || 0}</span>
                    {ec.quantidade_ja_produzida > 0 && (
                      <span className="text-blue-600">Feitas: <b>{ec.quantidade_ja_produzida}</b></span>
                    )}
                    {(ec.total_anomalias_lote || ec.total_anomalias_op || 0) > 0 && (
                      <span className="text-red-600">Anomalias: <b>{ec.total_anomalias_lote || ec.total_anomalias_op}</b></span>
                    )}
                  </div>
                </div>

                {/* Input de Quantidade - Compacto com +1/-1 */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-stone-500 w-16">Peças:</span>
                  <Button variant="outline" size="sm" onClick={() => setQuantidade(Math.max(0, (parseInt(quantidade) || 0) - 1).toString())}
                    className="h-8 w-8 p-0"><Minus className="h-3 w-3" /></Button>
                  <Input type="number" min="0" max={maxDisponivel} value={quantidade}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 0;
                      const anomalias = parseInt(quantidadeAnomalia) || 0;
                      const maxBoas = maxDisponivel - anomalias;
                      setQuantidade(Math.min(val, maxBoas).toString());
                    }} className="text-center text-lg font-bold h-8 w-20" placeholder="0" />
                  <Button variant="outline" size="sm" onClick={() => {
                    const anomalias = parseInt(quantidadeAnomalia) || 0;
                    const maxBoas = maxDisponivel - anomalias;
                    setQuantidade(Math.min(maxBoas, (parseInt(quantidade) || 0) + 1).toString());
                  }} className="h-8 w-8 p-0"><Plus className="h-3 w-3" /></Button>
                  <div className="flex gap-1 ml-2">
                    {[10, 50, 100].map(n => (
                      <Button key={n} variant="ghost" size="sm" onClick={() => {
                        const anomalias = parseInt(quantidadeAnomalia) || 0;
                        setQuantidade(Math.min(n, maxDisponivel - anomalias).toString());
                      }} className="text-xs h-6 px-2 text-stone-500 hover:text-stone-700">{n}</Button>
                    ))}
                    <Button variant="ghost" size="sm" onClick={() => {
                      const anomalias = parseInt(quantidadeAnomalia) || 0;
                      setQuantidade(Math.max(0, maxDisponivel - anomalias).toString());
                    }} className="text-xs h-6 px-2 text-green-600 font-medium">Máx</Button>
                  </div>
                </div>

                {/* Anomalias - Inline compacto com +1/-1 */}
                {(acaoEmCurso === 'anomalias' || comAnomalias) && (
                  <div className="flex items-center gap-2 bg-orange-50 rounded px-2 py-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 text-orange-500" />
                    <span className="text-xs text-orange-700">Anomalias:</span>
                    <Button variant="outline" size="sm" onClick={() => handleAnomaliaChange(Math.max(0, (parseInt(quantidadeAnomalia) || 0) - 1).toString())}
                      className="h-7 w-7 p-0"><Minus className="h-3 w-3" /></Button>
                    <Input type="number" min="0" value={quantidadeAnomalia} onChange={(e) => handleAnomaliaChange(e.target.value)}
                      className="w-16 h-7 text-center font-bold text-sm" placeholder="0" />
                    <Button variant="outline" size="sm" onClick={() => handleAnomaliaChange(((parseInt(quantidadeAnomalia) || 0) + 1).toString())}
                      className="h-7 w-7 p-0"><Plus className="h-3 w-3" /></Button>
                    <span className="text-[10px] text-orange-500">(classificar depois)</span>
                  </div>
                )}

                {/* Indicador peças boas e desvio */}
                {(parseInt(quantidadeAnomalia) || 0) > 0 && (
                  <div className="flex items-center gap-3 px-2">
                    <span className="text-xs text-stone-500">Peças boas: <b className="text-green-600">{Math.max(0, (parseInt(quantidade) || 0) - (parseInt(quantidadeAnomalia) || 0))}</b></span>
                    {Math.max(0, (parseInt(quantidade) || 0) - (parseInt(quantidadeAnomalia) || 0)) < (ec.quantidade_disponivel || 0) && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-red-100 text-red-700">DESVIO</span>
                    )}
                  </div>
                )}

                {/* Toggle Anomalias + Botões na mesma linha */}
                <div className="flex items-center gap-2 pt-1">
                  {acaoEmCurso !== 'anomalias' && (
                    <Button variant="ghost" size="sm" onClick={() => setComAnomalias(!comAnomalias)}
                      className={`text-xs h-8 ${comAnomalias ? 'text-orange-600 bg-orange-50' : 'text-stone-400'}`}>
                      {comAnomalias ? '✓ Anomalias' : '+ Anomalias'}
                    </Button>
                  )}
                  <div className="flex-1" />
                  <Button variant="outline" size="sm" onClick={resetForm} disabled={loading} className="h-8">Cancelar</Button>
                  <Button size="sm" className="h-8 bg-green-600 hover:bg-green-700 gap-1" onClick={handleConcluir} disabled={loading}>
                    {loading ? '...' : <><CheckCircle className="h-3.5 w-3.5" /> Confirmar ({Math.max(0, (parseInt(quantidade) || 0) - (parseInt(quantidadeAnomalia) || 0))})</>}
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-2 py-2 flex-wrap">
          {['lista', 'operacoes', 'pontuais', 'dias'].map(v => (
            <Button key={v} size="sm" variant={vistaAtiva === v ? 'default' : 'ghost'} onClick={() => setVistaAtiva(v)} className="gap-1" data-testid={`tab-${v}`}>
              {v === 'lista' && <><ListFilter className="h-4 w-4" />Lista ({itensCombinados.length})</>}
              {v === 'operacoes' && <><BarChart3 className="h-4 w-4" />Operações ({itensOperacoes.length})</>}
              {v === 'pontuais' && <><CheckCircle className="h-4 w-4" />Pontuais ({itensPontuais.length})</>}
              {v === 'dias' && <><CalendarDays className="h-4 w-4" />Dias ({dados?.por_dia?.length || 0})</>}
            </Button>
          ))}
        </div>

        {/* Conteúdo */}
        <div className="flex-1 overflow-y-auto min-h-[250px]">
          {loading && !dados ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <>
              {/* Lista */}
              {vistaAtiva === 'lista' && (
                <div className="space-y-2 py-2">
                  {!itensCombinados.length ? (
                    <div className="text-center py-8 text-stone-500"><Calendar className="h-10 w-10 mx-auto mb-2 opacity-50" /><p>Sem registos</p></div>
                  ) : itensCombinados.map(renderItem)}
                </div>
              )}

              {/* Operações */}
              {vistaAtiva === 'operacoes' && (
                <div className="space-y-2 py-2">
                  {!itensOperacoes.length ? (
                    <div className="text-center py-8 text-stone-500"><BarChart3 className="h-10 w-10 mx-auto mb-2 opacity-50" /><p>Sem operações</p></div>
                  ) : itensOperacoes.map(renderItem)}
                </div>
              )}

              {/* Pontuais */}
              {vistaAtiva === 'pontuais' && (
                <div className="space-y-2 py-2">
                  {!itensPontuais.length ? (
                    <div className="text-center py-8 text-stone-500"><CheckCircle className="h-10 w-10 mx-auto mb-2 opacity-50" /><p>Sem tarefas pontuais</p></div>
                  ) : itensPontuais.map(renderItem)}
                </div>
              )}
              {vistaAtiva === 'dias' && (
                <div className="space-y-1 py-2">
                  {!dados?.por_dia?.length ? (
                    <div className="text-center py-8 text-stone-500"><CalendarDays className="h-10 w-10 mx-auto mb-2 opacity-50" /><p>Sem registos</p></div>
                  ) : dados.por_dia.map((d, i) => {
                    const dt = new Date(d.data);
                    return (
                      <Card key={d.data || i} className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex flex-col items-center justify-center text-white">
                              <span className="text-[10px]">{['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'][dt.getDay()]}</span>
                              <span className="text-lg font-bold leading-none">{dt.getDate()}</span>
                            </div>
                            <div>
                              <p className="font-medium">{dt.toLocaleDateString('pt-PT', { weekday: 'long', day: 'numeric', month: 'short' })}</p>
                              <p className="text-xs text-stone-500">{d.registos} reg. • {d.operacoes_distintas} ops • {formatHora(d.hora_inicio)}-{formatHora(d.hora_fim)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-right">
                            <div><p className="font-bold text-lg text-green-600">{d.quantidade}</p><p className="text-xs text-stone-500">peças</p></div>
                            <div><p className="font-bold">{formatTempo(d.tempo_produtivo)}</p><p className="text-xs text-stone-500">produtivo</p></div>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center pt-2 border-t">
          <p className="text-sm text-stone-500">{dados?.periodo?.label}</p>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </div>
      </DialogContent>

      {/* Modal de Trocar Operação */}
      <TrocarOperacaoModal
        open={!!trocarOpRegisto}
        onOpenChange={(o) => { if (!o) setTrocarOpRegisto(null); }}
        registoId={trocarOpRegisto?.registo_id}
        registoInfo={trocarOpRegisto?.registo_info}
        onSuccess={() => { setTrocarOpRegisto(null); loadHistorico(); }}
      />
    </Dialog>
  );
}
